import { lazy, Suspense, useEffect, useState } from "react";
import { AppProvider } from "./state";
import { onSessionRevoked } from "./session";
import { wipePrivateData } from "./privateData";
import { ApiError } from "./api";
import { bindPrivateSession } from "./privacyEpoch";
import { registerServiceWorker } from "./swUpdate";
import { LocaleProvider } from "./i18n/LocaleProvider";
import { useApp } from "./state";

/* De shell blijft lazy: de entry-chunk bevat de wereld niet. */
const Adventure = lazy(() => import('./adventure/Adventure'));
const AtlasShell = lazy(() => import("./atlas/AtlasShell"));

/* Wist dit apparaat ooit een geldige sessie te hebben? Puur een hint voor
   welke shell we offline tonen — nooit een vorm van toegang. */
/* De naam hoort bij de app, niet bij de deur. index.html draagt een neutrale
   titel; deze komt er pas op zodra de sessie geldig is. */
const REVEAL_TITLE = "10 September";
const SESSION_HINT = "gorodn_known_session";
const knownSession = () => {
  try { return localStorage.getItem(SESSION_HINT) === "1"; } catch { return false; }
};
const rememberSession = (yes: boolean) => {
  try {
    if (yes) localStorage.setItem(SESSION_HINT, "1");
    else localStorage.removeItem(SESSION_HINT);
  } catch { /* private mode: dan gewoon zonder hint */ }
};

export function App() {
  const [phase, setPhase] = useState<"checking" | "invite" | "ready" | "locked">("checking");
  /* De tweede stadsweergave is vervallen. Ruim de oude, lokale voorkeur op
     zodat geen enkel apparaat een ongebruikte layoutvlag blijft dragen. */
  useEffect(() => {
    try { localStorage.removeItem("gorodn_layout"); } catch { /* private mode */ }
  }, []);
  /* Trekt de server de sessie in terwijl de app draait, dan sluit hij af:
     hint weg, lokale kopie weg, slotscherm. Anders bleef het private scherm
     staan met oude gegevens. */
  useEffect(() => onSessionRevoked(() => {
    /* Slot eerst -- de private UI moet meteen weg, ook als het opruimen even
       duurt. Daarna élk lokaal spoor: bootcache, Today Pass, cursors, en de
       IndexedDB met outbox en media-blobs. */
    setPhase("locked");
    void wipePrivateData();
  }), []);
  useEffect(() => {
    (async () => {
      /* Bestaande sessie wint altijd — voorkomt een tweede redeem na reload. */
      let me: Response | null = null;
      try {
        me = await fetch("/api/v1/me", { credentials: "same-origin" });
      } catch {
        /* Netwerk weg. Dit is geen «geen toegang»: een koude start in de
           metro hoorde nooit het slotscherm te tonen. Kende dit apparaat al
           een sessie, dan openen we gewoon de private shell; alles daarbinnen
           valt terug op cache en outbox. De server blijft de enige die echt
           toegang verleent — de eerste geslaagde call die 401 geeft, sluit
           alsnog af. */
        setPhase(knownSession() ? "ready" : "locked");
        return;
      }
      if (me.ok) {
        bindPrivateSession();
        rememberSession(true);
        document.title = REVEAL_TITLE;
        if (location.hash.includes("k="))
          history.replaceState(null, "", `/${location.search}`);
        setPhase("ready"); return;
      }
          /* Alleen 401 betekent «geen geldige sessie». */
      if (me.status === 401) {
        // Finish clearing the previous account's drafts/media/outbox before an
        // invitation can establish another account in this browser.
        rememberSession(false);
        await wipePrivateData();
      }

      /* sleutel in het fragment (#k=…): verlaat de browser nooit */
      const frag = new URLSearchParams(location.hash.slice(1));
      const token = frag.get("k");
      if (token && /^[A-Za-z0-9_-]{16,128}$/.test(token)) {
        sessionStorage.setItem("gorodn_redeeming", "1");
        const r = await fetch("/api/v1/auth/redeem", {
          method: "POST", credentials: "same-origin",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ token, deviceName: navigator.platform || "device" }),
        });
        sessionStorage.removeItem("gorodn_redeeming");
        /* De shellvlag staat in de query en moet de redeem overleven. */
        history.replaceState(null, "", `/${location.search}`);
        if (r.ok) {
          bindPrivateSession();
          rememberSession(true);
          document.title = REVEAL_TITLE;
          /* main.tsx slaat registratie over zolang er een redeem loopt (de
             worker mag daar niet tussendoor komen). Nu die klaar is, alsnog
             registreren -- anders heeft een vers uitgenodigd apparaat pas na
             een tweede koude start offline-ondersteuning. */
          if (import.meta.env.PROD) registerServiceWorker();
        }
        setPhase(r.ok ? "ready" : "locked"); return;
      }
      setPhase("locked");
    })().catch((e) => setPhase(e instanceof ApiError ? "locked" : "locked"));
  }, []);

  if (phase === "checking")
    return <div className="bootscreen"><span className="crt">·</span></div>;
  if (phase === "locked")
    /* bewust anoniem: zonder sleutel geen naam, geen hint — de onthulling
       is pas in Москва (de graveerkaart ís de deur) */
    return <div className="bootscreen locked" aria-label="closed">
      <p className="lockedsub lockeddot">·</p>
    </div>;
  return (
    <AppProvider>
      {/* De taal hangt binnen AppProvider, want wie je bent bepaalt de
          standaardtaal -- en dat weten we pas als bootstrap binnen is. */}
      <Localized>
        <Suspense fallback={<div className="bootscreen"><span className="crt">·</span></div>}>
          {location.pathname.replace(/\/$/, '') === '/adventure' ? <Adventure /> : <AtlasShell />}
        </Suspense>
      </Localized>
    </AppProvider>);
}

/** Koppelt de taalkeuze aan de ingelogde gebruiker: R leest standaard Engels,
 *  Н standaard Russisch. Een eigen keuze wint daar altijd van. */
function Localized({ children }: { children: React.ReactNode }) {
  const { boot } = useApp();
  return <LocaleProvider handle={boot?.me.handle ?? null}>{children}</LocaleProvider>;
}
